import { initializeApp, getApps } from 'firebase/app';
import {
  getFirestore,
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  getDocs,
  limit,
  limitToLast,
  endBefore,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import {
  browserLocalPersistence,
  getAuth,
  inMemoryPersistence,
  initializeAuth,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import * as Sentry from '@sentry/react';

// Firebase config is loaded from environment variables (Vite)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

const firestoreDb = getFirestore(app);
const getFirebaseAuth = () => {
  try {
    return initializeAuth(app, {
      persistence: [browserLocalPersistence, inMemoryPersistence],
    });
  } catch (err) {
    if (err?.code === 'auth/already-initialized') {
      return getAuth(app);
    }

    throw err;
  }
};

const firebaseAuth = getFirebaseAuth();

let activeAdapter = null;

/**
 * Sign out of Firebase if there is a current session.
 * Stops all snapshot listeners before signing out to prevent
 * permission errors from listeners firing against a signed-out state.
 * Best-effort: errors are reported to Sentry but never block the caller.
 */
export function firebaseSignOut() {
  if (activeAdapter) {
    activeAdapter.destroy();
    activeAdapter = null;
  }
  if (firebaseAuth.currentUser) {
    return signOut(firebaseAuth).catch((err) => {
      Sentry.captureException(err);
    });
  }
  return Promise.resolve();
}

/**
 * Normalize a Firestore conversation document.
 */
function normalizeConversation(docData, participantDoc) {
  const lastMsg = docData.lastMessage;
  return {
    id: docData.conversationId,
    type: (docData.type || 'donation').toUpperCase(),
    friendlyName: docData.friendlyName,
    teamId: docData.teamId || null,
    donationId: docData.donationId || null,
    listingId: docData.listingId || null,
    // Marketplace listing conversations carry the seller's team id so the UI
    // can tell whether the viewer is the seller or the buyer. Older docs may
    // not have this field; keep it as '' so consumers can fall back to the
    // legacy "show everywhere" behavior.
    sellerTeamId: docData.sellerTeamId || '',
    dateCreated: docData.dateCreated?.toDate?.() || new Date(),
    dateUpdated: docData.dateUpdated?.toDate?.() || new Date(),
    lastMessage: lastMsg
      ? {
          text: lastMsg.text,
          authorName: lastMsg.authorName,
          authorIdentity: lastMsg.authorIdentity,
          media: Array.isArray(lastMsg.media) ? lastMsg.media : [],
          dateCreated: lastMsg.timestamp?.toDate?.() || new Date(),
        }
      : null,
    lastReadTimestamp: participantDoc?.lastRead?.toDate?.() || null,
    muted: participantDoc?.muted || false,
    archived: docData.archived || false,
  };
}

/**
 * Normalize a Firestore message document.
 */
function normalizeMessage(msgData) {
  return {
    id: msgData.messageId,
    text: msgData.text,
    authorIdentity: msgData.authorIdentity,
    authorName: msgData.authorName,
    // Photo attachments: array of { url, contentType, filename } where `url`
    // is an S3 object key (rendered through getS3Url) or an absolute URL.
    media: Array.isArray(msgData.media) ? msgData.media : [],
    timestamp: msgData.timestamp?.toDate?.() || new Date(),
  };
}

/**
 * Convert a Careit identity (UID456~TID123) to the Firebase-safe UID format
 * used as participant doc keys (UID456_TID123).
 */
function toFirebaseUid(identity) {
  return identity.replace(/~/g, '_');
}

// Participant events can race with cleanup or membership changes, so these
// read failures usually mean the conversation is already gone or unreadable.
function isStaleConversationReadError(err) {
  const code = err?.code;
  return code === 'permission-denied' || code === 'not-found';
}

/**
 * FirestoreAdapter uses the Firebase JS SDK to provide real-time chat
 * functionality from Firestore.
 */
export default class FirestoreAdapter {
  constructor() {
    this.identity = null; // Original format: UID456~TID123
    this.firebaseUid = null; // Firebase-safe format: UID456_TID123
    this.conversations = {}; // id -> NormalizedConversation
    this.participants = {}; // conversationId -> [participant docs]
    this._unreadCounts = {}; // conversationId -> number
    this._unsubscribers = [];
    this._destroyed = false;
    this._authUnsubscriber = null;
    this._listeners = {
      conversationsUpdated: [],
      participantsUpdated: [],
      messageAdded: [],
    };
  }

  /**
   * Initialize the adapter by authenticating with Firebase and setting up
   * real-time listeners for conversations the user participates in.
   *
   * @param {string} token - Firebase custom token from backend
   * @param {string} identity - User identity (e.g., "UID456~TID123")
   */
  async init(token, identity, { onRefreshToken } = {}) {
    try {
      this.identity = identity;
      this.firebaseUid = toFirebaseUid(identity);

      // Authenticate with Firebase
      let currentToken = token;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await signInWithCustomToken(firebaseAuth, currentToken);
          break;
        } catch (err) {
          const retryable =
            err.code === 'auth/invalid-custom-token' ||
            err.code === 'auth/network-request-failed';
          if (retryable && onRefreshToken && attempt < 2) {
            currentToken = await onRefreshToken();
          } else {
            throw err;
          }
        }
      }

      if (this._destroyed) return;

      activeAdapter = this;

      // When the Firebase user drops (sign-out, token expiry), tear down
      // all snapshot listeners so they don't fire against a null auth state.
      this._authUnsubscriber = onAuthStateChanged(firebaseAuth, (user) => {
        if (!user && !this._destroyed) {
          this.destroy();
        }
      });

      // Query all conversations where this user is a participant
      // We find participant docs matching the identity, then load parent conversations
      await this._subscribeToConversations();
    } catch (err) {
      Sentry.captureException(err, {
        contexts: {
          firebase: { token },
        },
      });
      throw err;
    }
  }

  async _subscribeToConversations() {
    if (!this.firebaseUid || this._destroyed) return;
    // Use collectionGroup query on participants to find all conversations
    // where this user is a member. We query by `uid` (firebase-safe format)
    // so Firestore security rules can verify the query against request.auth.uid.
    const participantsQuery = query(
      collectionGroup(firestoreDb, 'participants'),
      where('uid', '==', this.firebaseUid)
    );

    // Hold init() until the first Firestore snapshot is processed so that
    // conversations are populated before the caller marks loading as done.
    // Without this, onSnapshot returns immediately and init() resolves with
    // empty data, causing a brief empty-screen flash on fast navigations.
    let resolveFirstLoad;
    const firstLoadPromise = new Promise((resolve) => {
      resolveFirstLoad = resolve;
    });

    const unsub = onSnapshot(
      participantsQuery,
      async (snapshot) => {
        for (const change of snapshot.docChanges()) {
          const participantData = change.doc.data();
          // The participant doc path is: conversations/{convId}/participants/{identity}
          const convRef = change.doc.ref.parent.parent;
          const convId = convRef.id;

          if (change.type === 'removed') {
            this._removeConversation(convId);
          } else {
            await this._processConversationUpdate(
              participantData,
              convRef,
              convId
            );
          }
        }

        if (!this._destroyed) {
          this._emit('conversationsUpdated', { ...this.conversations });
        }

        // Resolve only on the first snapshot; subsequent snapshots are
        // real-time updates and don't need to gate init().
        if (resolveFirstLoad) {
          resolveFirstLoad();
          resolveFirstLoad = null;
        }
      },
      (err) => {
        // Auth is gone or switched — tear down instead of reporting.
        if (isStaleConversationReadError(err)) {
          this.destroy();
        } else {
          Sentry.captureException(err);
        }
        // Resolve on error too so init() doesn't hang indefinitely.
        if (resolveFirstLoad) {
          resolveFirstLoad();
          resolveFirstLoad = null;
        }
      }
    );

    this._unsubscribers.push(unsub);
    return firstLoadPromise;
  }

  /**
   * Load or refresh a conversation after its participant doc was added or
   * modified.
   */
  async _processConversationUpdate(participantData, convRef, convId) {
    try {
      const convSnap = await getDoc(convRef);
      if (this._destroyed || !convSnap.exists()) {
        this._removeConversation(convId);
        return;
      }

      const convData = convSnap.data();
      const normalized = normalizeConversation(convData, participantData);

      // Compute initial unread count from timestamp comparison
      if (this._unreadCounts[convId] === undefined) {
        const hasUnread =
          normalized.lastMessage &&
          (!normalized.lastReadTimestamp ||
            new Date(normalized.lastMessage.dateCreated) >
              new Date(normalized.lastReadTimestamp));
        this._unreadCounts[convId] = hasUnread ? 1 : 0;
      }
      normalized.unreadCount = this._unreadCounts[convId];
      this.conversations[convId] = normalized;

      // Subscribe to messages for active conversations only
      if (!convData.archived) {
        this._subscribeToMessages(convId);
      }
      // Load participants
      this._loadParticipants(convId);
    } catch (err) {
      if (isStaleConversationReadError(err)) {
        this._removeConversation(convId);
        return;
      }

      Sentry.captureException(err);
    }
  }

  /**
   * Drop all local state and listeners for a conversation the user no longer
   * has access to.
   */
  _removeConversation(conversationId) {
    const messageUnsub = this._messageUnsubscribers[conversationId];
    if (messageUnsub) {
      messageUnsub();
      delete this._messageUnsubscribers[conversationId];
      this._unsubscribers = this._unsubscribers.filter(
        (unsub) => unsub !== messageUnsub
      );
    }

    delete this.conversations[conversationId];
    delete this.participants[conversationId];
    delete this._unreadCounts[conversationId];

    if (!this._destroyed) {
      this._emit('conversationsUpdated', { ...this.conversations });
      this._emit('participantsUpdated', { ...this.participants });
    }
  }

  _messageUnsubscribers = {};

  _subscribeToMessages(conversationId) {
    // Don't duplicate subscriptions
    if (this._messageUnsubscribers[conversationId]) return;

    const messagesRef = collection(
      firestoreDb,
      'conversations',
      conversationId,
      'messages'
    );
    const messagesQuery = query(
      messagesRef,
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    let isInitialSnapshot = true;

    const unsub = onSnapshot(
      messagesQuery,
      (snapshot) => {
        try {
          for (const change of snapshot.docChanges()) {
            if (change.type === 'added') {
              const msgData = change.doc.data();
              const normalized = normalizeMessage(msgData);

              // Update the conversation's lastMessage so the list UI reflects the new message
              if (this.conversations[conversationId]) {
                this.conversations[conversationId].lastMessage = {
                  text: normalized.text,
                  authorName: normalized.authorName,
                  authorIdentity: normalized.authorIdentity,
                  media: normalized.media,
                  dateCreated: normalized.timestamp,
                };
                this.conversations[conversationId].dateUpdated =
                  normalized.timestamp;

                // Increment unread count for new messages from other users (skip initial snapshot)
                if (
                  !isInitialSnapshot &&
                  normalized.authorIdentity !== this.identity
                ) {
                  this._unreadCounts[conversationId] =
                    (this._unreadCounts[conversationId] || 0) + 1;
                }
                this.conversations[conversationId].unreadCount =
                  this._unreadCounts[conversationId] || 0;

                this._emit('conversationsUpdated', { ...this.conversations });
              }

              // Only emit messageAdded for new messages, not initial snapshot
              if (!isInitialSnapshot) {
                this._emit('messageAdded', {
                  conversationId,
                  message: normalized,
                });
              }
            }
          }
          isInitialSnapshot = false;
        } catch (err) {
          Sentry.captureException(err);
        }
      },
      (err) => {
        // Lost access to this conversation — drop it instead of reporting.
        if (isStaleConversationReadError(err)) {
          this._removeConversation(conversationId);
          return;
        }
        Sentry.captureException(err);
      }
    );

    this._messageUnsubscribers[conversationId] = unsub;
    this._unsubscribers.push(unsub);
  }

  async _loadParticipants(conversationId) {
    try {
      const participantsRef = collection(
        firestoreDb,
        'conversations',
        conversationId,
        'participants'
      );
      const snapshot = await getDocs(participantsRef);
      const participants = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          identity: data.identity,
          name: data.name,
        };
      });
      this.participants[conversationId] = participants;
      this._emit('participantsUpdated', { ...this.participants });
    } catch (err) {
      if (isStaleConversationReadError(err)) {
        this._removeConversation(conversationId);
        return;
      }
      Sentry.captureException(err);
    }
  }

  /**
   * Get messages for a Firestore conversation with pagination.
   * Fetches the latest PAGE_SIZE messages first, with prevPage() to load older ones.
   * @param {string} conversationId
   * @param {number} pageSize
   * @returns {Promise<{items: NormalizedMessage[], hasPrevPage: boolean, prevPage: Function}>}
   */
  async getMessages(conversationId, pageSize = 30) {
    const messagesRef = collection(
      firestoreDb,
      'conversations',
      conversationId,
      'messages'
    );

    // Fetch latest pageSize+1 messages to check if there are older ones
    const messagesQuery = query(
      messagesRef,
      orderBy('timestamp', 'asc'),
      limitToLast(pageSize + 1)
    );
    const snapshot = await getDocs(messagesQuery);
    const docs = snapshot.docs;
    const hasMore = docs.length > pageSize;
    const pageDocs = hasMore ? docs.slice(1) : docs;
    const items = pageDocs.map((d) => normalizeMessage(d.data()));

    const firstDoc = pageDocs[0];

    return {
      items,
      hasPrevPage: hasMore,
      prevPage: hasMore
        ? () => this._getPrevMessages(messagesRef, firstDoc, pageSize)
        : async () => ({ items: [], hasPrevPage: false, prevPage: null }),
    };
  }

  async _getPrevMessages(messagesRef, beforeDoc, pageSize) {
    const messagesQuery = query(
      messagesRef,
      orderBy('timestamp', 'asc'),
      endBefore(beforeDoc),
      limitToLast(pageSize + 1)
    );
    const snapshot = await getDocs(messagesQuery);
    const docs = snapshot.docs;
    const hasMore = docs.length > pageSize;
    const pageDocs = hasMore ? docs.slice(1) : docs;
    const items = pageDocs.map((d) => normalizeMessage(d.data()));

    const firstDoc = pageDocs[0];

    return {
      items,
      hasPrevPage: hasMore,
      prevPage: hasMore
        ? () => this._getPrevMessages(messagesRef, firstDoc, pageSize)
        : async () => ({ items: [], hasPrevPage: false, prevPage: null }),
    };
  }

  /**
   * Send a message to a Firestore conversation.
   *
   * @param {string} conversationId
   * @param {string} text
   * @param {string} [authorName]
   * @param {Array<{url: string, contentType: string, filename: string}>} [media]
   *   Optional photo attachments. Each `url` is an S3 object key (uploaded
   *   beforehand through the standard signed-url flow) or an absolute URL.
   */
  async sendMessage(conversationId, text, authorName, media) {
    try {
      const messagesRef = collection(
        firestoreDb,
        'conversations',
        conversationId,
        'messages'
      );

      const mediaList = Array.isArray(media) ? media : [];
      const messageId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const messageData = {
        messageId,
        text,
        authorName: authorName || this.identity,
        authorIdentity: this.identity,
        timestamp: serverTimestamp(),
      };
      if (mediaList.length > 0) {
        messageData.media = mediaList;
      }

      await addDoc(messagesRef, messageData);

      // Update conversation's lastMessage and dateUpdated
      const convRef = doc(firestoreDb, 'conversations', conversationId);
      await updateDoc(convRef, {
        lastMessage: {
          text,
          authorName: authorName || this.identity,
          authorIdentity: this.identity,
          ...(mediaList.length > 0 && { media: mediaList }),
          timestamp: Timestamp.now(),
        },
        dateUpdated: serverTimestamp(),
      });

      // Update participant's lastRead
      await this.markRead(conversationId);
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  }

  /**
   * Mark messages as read by updating the participant's lastRead timestamp.
   */
  async markRead(conversationId) {
    if (!this.firebaseUid) return;
    const participantRef = doc(
      firestoreDb,
      'conversations',
      conversationId,
      'participants',
      this.firebaseUid
    );
    try {
      await updateDoc(participantRef, {
        lastRead: serverTimestamp(),
        lastReadMessageId: null, // Can be enhanced later
      });
    } catch (err) {
      Sentry.captureException(err);
    }

    // Reset local unread count
    this._unreadCounts[conversationId] = 0;
    if (this.conversations[conversationId]) {
      this.conversations[conversationId].unreadCount = 0;
      this._emit('conversationsUpdated', { ...this.conversations });
    }
  }

  /**
   * Toggle mute for the current user in a conversation. The mute flag is
   * persisted on the user's own participant doc (allowed by the Firestore
   * rules alongside lastRead) and mirrored into the local conversation cache
   * so the UI reflects the new state without waiting for the participants
   * snapshot round trip.
   *
   * @returns {Promise<boolean|undefined>} The new muted state.
   */
  async toggleMute(conversationId) {
    if (!this.firebaseUid) return undefined;
    const conv = this.conversations[conversationId];
    const isMuted = conv?.muted || false;

    try {
      const participantRef = doc(
        firestoreDb,
        'conversations',
        conversationId,
        'participants',
        this.firebaseUid
      );
      await updateDoc(participantRef, {
        muted: !isMuted,
        mutedAt: !isMuted ? serverTimestamp() : null,
      });
    } catch (err) {
      Sentry.captureException(err);
      // Surface the failure so callers can show an error instead of
      // pretending the toggle succeeded.
      throw err;
    }

    if (conv) {
      conv.muted = !isMuted;
      this._emit('conversationsUpdated', { ...this.conversations });
    }
    return !isMuted;
  }

  /**
   * Subscribe to adapter events.
   */
  on(event, callback) {
    if (this._listeners[event]) {
      this._listeners[event].push(callback);
    }
  }

  off(event, callback) {
    if (this._listeners[event]) {
      this._listeners[event] = this._listeners[event].filter(
        (cb) => cb !== callback
      );
    }
  }

  _emit(event, data) {
    if (this._listeners[event]) {
      this._listeners[event].forEach((cb) => cb(data));
    }
  }

  /**
   * Shutdown and clean up all Firestore listeners.
   */
  destroy() {
    this._destroyed = true;
    if (this._authUnsubscriber) {
      this._authUnsubscriber();
      this._authUnsubscriber = null;
    }
    this._unsubscribers.forEach((unsub) => unsub());
    this._unsubscribers = [];
    this._messageUnsubscribers = {};
    this._unreadCounts = {};
    this.conversations = {};
    this.participants = {};
    this.identity = null;
    this.firebaseUid = null;
    this._listeners = {
      conversationsUpdated: [],
      participantsUpdated: [],
      messageAdded: [],
    };
    if (activeAdapter === this) {
      activeAdapter = null;
    }
  }

  isReady() {
    return !!this.identity;
  }
}

export { firestoreDb, firebaseAuth };
